// src/routes/index.js
import { lazy } from "react";
import { Navigate } from "react-router-dom";
import { PublicLayout } from "../layouts/PublicLayout";
import ProtectedLayout from "../layouts/ProtectedLayout"; // 🚀 NEW: Modern layout-based protection
import { ProtectedRoute } from "../components/ProtectedRoute"; // 🔐 Route-level protection
import { ROLES } from "../api/constants";

// 🛡️ ERROR PAGES: Import error pages for robust error handling
import Error401 from "../features/errors/pages/Error401";
import Error403 from "../features/errors/pages/Error403";
import Error404 from "../features/errors/pages/Error404";
import Error500 from "../features/errors/pages/Error500";
import Error503 from "../features/errors/pages/Error503";

// Constants
const PATHS = {
  ROOT: "/",
  ABOUT: "/about",
  PRICING: "/pricing",
  LOGIN: "/login",
  VERIFY: "/verify",
  POLICY: "/policy",
  KVKK: "/kvkk",
  GO: "/go",
  ACCESS_DENIED: "/access-denied",
  // 🛡️ ERROR PATHS: Dedicated routes for different error scenarios
  UNAUTHORIZED: "/unauthorized",
  FORBIDDEN: "/forbidden",
  NOT_FOUND: "/not-found",
  SERVER_ERROR: "/server-error",
  SERVICE_UNAVAILABLE: "/service-unavailable",
  ADMIN: {
    ROOT: "/admin",
    DASHBOARD: "/dashboard",
    PROFILE: "/admin/profile", // Admin profili
    COMPANIES: {
      ROOT: "/admin/company-management",
      ADD: "/admin/company-management/add",
      EDIT: "/admin/company-management/edit/:id",
      VIEW: "/admin/company-management/:id",
    },
    ANNOUNCEMENTS: {
      ROOT: "/admin/announcement-management",
      ADD: "/admin/announcement-management/add",
      EDIT: "/admin/announcement-management/edit/:id",
      VIEW: "/admin/announcement-management/:id",
    },
    BRANDS: {
      ROOT: "/admin/brand-management",
      ADD: "/admin/brand-management/add",
      EDIT: "/admin/brand-management/edit/:id",
      VIEW: "/admin/brand-management/:id",
    },
    CAMPAIGNS: {
      ROOT: "/admin/campaigns",
      ADD: "/admin/campaigns/add",
      EDIT: "/admin/campaigns/edit/:id",
      VIEW: "/admin/campaigns/:id",
    },
    DRAWS: {
      ROOT: "/admin/draws",
      ADD: "/admin/draws/add",
      EDIT: "/admin/draws/edit/:id",
      VIEW: "/admin/draws/:id",
    },
    VIDEOS: {
      ROOT: "/admin/videos",
      ADD: "/admin/videos/add",
      EDIT: "/admin/videos/edit/:id",
      VIEW: "/admin/videos/:id",
    },
    SURVEY: {
      ROOT: "/admin/survey-management",
      ADD: "/admin/survey-management/add",
      EDIT: "/admin/survey-management/edit/:id",
      VIEW: "/admin/survey-management/:id",
      RECOMMENDATION: "/admin/survey-management/recommendation/:id",
    },
    PAYMENT: {
      ROOT: "/admin/payment-management",
      ADD: "/admin/payment-management/add",
      EDIT: "/admin/payment-management/edit/:id",
      VIEW: "/admin/payment-management/:id",
    },
    GIFT: {
      ROOT: "/admin/gift-management",
      ADD: "/admin/gift-management/add",
      EDIT: "/admin/gift-management/edit/:id",
      VIEW: "/admin/gift-management/:id",
    },
    REPORTS: "/admin/reports",
    TICKETS: {
      ROOT: "/admin/tickets-management",
      ADD: "/admin/tickets-management/add",
      VIEW: "/admin/tickets-management/:id",
    },
  },
  TENANT_ADMIN: {
    ROOT: "/user",
    DASHBOARD: "/dashboard",
    PROFILE: "/user/profile", // TenantAdmin profili
    CAMPAIGNS: {
      ROOT: "/user/campaigns",
      ADD: "/user/campaigns/add",
      EDIT: "/user/campaigns/edit/:id",
      VIEW: "/user/campaigns/:id",
    },
    DRAWS: {
      ROOT: "/user/draws",
      ADD: "/user/draws/add",
      EDIT: "/user/draws/edit/:id",
      VIEW: "/user/draws/:id",
    },
    VIDEOS: {
      ROOT: "/user/videos",
      ADD: "/user/videos/add",
      EDIT: "/user/videos/edit/:id",
      VIEW: "/user/videos/:id",
    },
    COMPANY_SUBTENANTS: {
      ROOT: "/user/company-subTenants",
      ADD: "/user/company-subTenants/add",
      EDIT: "/user/company-subTenants/edit/:id",
      VIEW: "/user/company-subTenants/:id",
    },
    ANNOUNCEMENTS: {
      ROOT: "/user/announcement-management",
      ADD: "/user/announcement-management/add",
      EDIT: "/user/announcement-management/edit/:id",
      VIEW: "/user/announcement-management/:id",
    },
    SURVEY: {
      ROOT: "/user/survey-management",
      ADD: "/user/survey-management/add",
      EDIT: "/user/survey-management/edit/:id",
      VIEW: "/user/survey-management/:id",
    },
    BRANDS: {
      ROOT: "/user/brand-management",
      ADD: "/user/brand-management/add",
      EDIT: "/user/brand-management/edit/:id",
      VIEW: "/user/brand-management/:id",
    },
    PAYMENT: {
      ROOT: "/user/payment-management",
      ADD: "/user/payment-management/add",
      ADD_TRANSFER: "/user/payment-management/add-transfer",
      EDIT: "/user/payment-management/edit/:id",
      VIEW: "/user/payment-management/:id",
    },
    GIFT: {
      ROOT: "/user/gift-management",
      ADD: "/user/gift-management/add",
      EDIT: "/user/gift-management/edit/:id",
      VIEW: "/user/gift-management/:id",
    },
    REPORTS: "/user/reports",
    TENANT_ADMINS: "/user/users",
    USERS: {
      ROOT: "/user/users",
      ADD: "/user/users/add",
      EDIT: "/user/users/edit/:id",
      VIEW: "/user/users/view/:id",
      BULK_UPLOAD: "/user/users/bulk-upload",
      BIRTHDAYS: "/user/users/birthdays",
    },
    TICKETS: {
      ROOT: "/user/tickets-management",
      ADD: "/user/tickets-management/add",
      VIEW: "/user/tickets-management/:id",
    },
  },
  USER: {
    ROOT: "/user",
    DASHBOARD: "/dashboard",
    PROFILE: "/user/profile",
    // Permission tabanlı erişim için modül bazlı path'ler - TENANT_ADMIN ile aynı sayfalar
    CAMPAIGNS: {
      ROOT: "/user/campaigns",
      ADD: "/user/campaigns/add",
      EDIT: "/user/campaigns/edit/:id",
      VIEW: "/user/campaigns/:id",
    },
    ANNOUNCEMENTS: {
      ROOT: "/user/announcements",
      ADD: "/user/announcements/add",
      EDIT: "/user/announcements/edit/:id",
      VIEW: "/user/announcements/:id",
    },
    SURVEYS: {
      ROOT: "/user/surveys",
      ADD: "/user/surveys/add",
      EDIT: "/user/surveys/edit/:id",
      VIEW: "/user/surveys/:id",
    },
    DRAWS: {
      ROOT: "/user/draws",
      ADD: "/user/draws/add",
      EDIT: "/user/draws/edit/:id",
      VIEW: "/user/draws/:id",
    },
    BRANDS: {
      ROOT: "/user/brands",
      ADD: "/user/brands/add",
      EDIT: "/user/brands/edit/:id",
      VIEW: "/user/brands/:id",
    },
    REPORTS: "/user/reports",
  },
};

// Lazy loaded components with meaningful grouping
const PageComponents = {
  Public: {
    Login: lazy(() => import("../features/login/pages/LoginPage")),
    Verify: lazy(() => import("../features/verify/pages/VerifyPage")),
    Go: lazy(() => import("../pages/GoPage")),
    AccessDenied: lazy(() => import("../features/errors/pages/AccessDenied")),
  },
  Admin: {
    Dashboard: lazy(() => import("../features/dashboard/pages/Dashboard")),
    Profile: lazy(() => import("../components/ProfileManagement")),
    Announcements: {
      List: lazy(() =>import("../features/announcements/pages/AnnouncementList")),
      Add: lazy(() =>import("../features/announcements/pages/AddAnnouncement")),
      Edit: lazy(() =>import("../features/announcements/pages/EditAnnouncement")),
      View: lazy(() =>import("../features/announcements/pages/ViewAnnouncement")),
    },
    Campaigns: {
      List: lazy(() => import("../features/campaigns/pages/CampaignList")),
      Add: lazy(() => import("../features/campaigns/pages/AddCampaign")),
      Edit: lazy(() => import("../features/campaigns/pages/EditCampaign")),
      View: lazy(() => import("../features/campaigns/pages/ViewCampaign")),
    },
    Companies: {
      List: lazy(() => import("../features/tenants/pages/TenantsList")),
      Add: lazy(() => import("../features/tenants/pages/AddTenants")),
      Edit: lazy(() => import("../features/tenants/pages/EditTenants")),
      View: lazy(() => import("../features/tenants/pages/ViewTenants")),
    },
    Draws: {
      List: lazy(() => import("../features/draws/pages/DrawList")),
      Add: lazy(() => import("../features/draws/pages/AddDraw")),
      Edit: lazy(() => import("../features/draws/pages/EditDraw")),
      View: lazy(() => import("../features/draws/pages/ViewDraw")),
    },
    Videos: {
      List: lazy(() => import("../features/videos/pages/VideosList")),
      Add: lazy(() => import("../features/videos/pages/AddVideos")),
      Edit: lazy(() => import("../features/videos/pages/EditVideos")),
      View: lazy(() => import("../features/videos/pages/ViewVideos")),
    },
    Tickets: {
      List: lazy(() => import("../features/tickets/pages/TicketsList")),
      Add: lazy(() => import("../features/tickets/pages/AddTickets")),
      View: lazy(() => import("../features/tickets/pages/ViewTickets")),
    },
    Gift: {
      List: lazy(() => import("../features/gifts/pages/GiftList")),
      Add: lazy(() => import("../features/gifts/pages/AddGift")),
      Edit: lazy(() => import("../features/gifts/pages/EditGift")),
      View: lazy(() => import("../features/gifts/pages/ViewGift")),
    },
    Brands: {
      List: lazy(() => import("../features/brands/pages/BrandList")),
      Add: lazy(() => import("../features/brands/pages/AddBrand")),
      Edit: lazy(() => import("../features/brands/pages/EditBrand")),
      View: lazy(() => import("../features/brands/pages/ViewBrand")),
    },
    Payment: {
      List: lazy(() => import("../features/payments/pages/PaymentList")),
      Add: lazy(() => import("../features/payments/pages/AddPayment")),
      Edit: lazy(() => import("../features/payments/pages/EditPayment")),
      View: lazy(() => import("../features/payments/pages/ViewPayment")),
    },
    Reports: lazy(() => import("../features/reports/pages/ReportsList")),
    Surveys: {
      List: lazy(() => import("../features/surveys/pages/SurveyList")),
      Add: lazy(() => import("../features/surveys/pages/AddSurvey")),
      Edit: lazy(() => import("../features/surveys/pages/EditSurvey")),
      View: lazy(() => import("../features/surveys/pages/ViewSurvey")),
      Recommendation: lazy(() =>import("../features/surveys/components/RecommendationSurvey")),
    },
  },
  TenantAdmin: {
    Dashboard: lazy(() => import("../features/dashboard/pages/Dashboard")),
    
    // 🚀 UNIFIED COMPONENTS - Same components as Admin, but with role-based conditional rendering
    Announcements: {
      List: lazy(() => import("../features/announcements/pages/AnnouncementList")),
      Add: lazy(() => import("../features/announcements/pages/AddAnnouncement")),
      Edit: lazy(() => import("../features/announcements/pages/EditAnnouncement")),
      View: lazy(() => import("../features/announcements/pages/ViewAnnouncement")),
    },
    Profile: lazy(() => import("../components/ProfileManagement")),
    Campaigns: {
      List: lazy(() =>import("../features/campaigns/pages/CampaignList")),
      Add: lazy(() => import("../features/campaigns/pages/AddCampaign")),
      Edit: lazy(() =>import("../features/campaigns/pages/EditCampaign")),
      View: lazy(() =>import("../features/campaigns/pages/ViewCampaign")),
    },
    Draws: {
      List: lazy(() => import("../features/draws/pages/DrawList")),
      Add: lazy(() => import("../features/draws/pages/AddDraw")),
      Edit: lazy(() => import("../features/draws/pages/EditDraw")),
      View: lazy(() => import("../features/draws/pages/ViewDraw")),
    },
    Videos: {
      List: lazy(() => import("../features/videos/pages/VideosList")),
      Add: lazy(() => import("../features/videos/pages/AddVideos")),
      Edit: lazy(() => import("../features/videos/pages/EditVideos")),
      View: lazy(() => import("../features/videos/pages/ViewVideos")),
    },
    Tickets: {
      List: lazy(() => import("../features/tickets/pages/TicketsList")),
      Add: lazy(() =>import("../features/tickets/pages/AddTickets")),
      View: lazy(() =>import("../features/tickets/pages/ViewTickets")),
    },
    Gift: {
      List: lazy(() => import("../features/gifts/pages/CompanyGiftList")),
      Add: lazy(() => import("../features/gifts/pages/AddGift")),
      Edit: lazy(() => import("../features/gifts/pages/EditGift")),
      View: lazy(() => import("../features/gifts/pages/ViewGift")),
    },
    Brands: {
      List: lazy(() => import("../features/brands/pages/BrandList")), // 🚀 UNIFIED RBAC
      Add: lazy(() =>import("../features/brands/pages/AddBrand")), // 🚀 UNIFIED RBAC
      Edit: lazy(() =>import("../features/brands/pages/EditBrand")), // 🚀 UNIFIED RBAC
      View: lazy(() =>import("../features/brands/pages/ViewBrand")), // 🚀 UNIFIED RBAC
    },
    CompanysubTenants: {
      List: lazy(() => import("../features/subTenants/pages/CompanysubTenantsList")),  
      Add: lazy(() => import("../features/subTenants/pages/CompanyAddsubTenants")),
      Edit: lazy(() =>import("../features/subTenants/pages/CompanyEditsubTenants")),
      View: lazy(() => import("../features/subTenants/pages/CompanyViewsubTenants")),
    },
    Reports: lazy(() => import("../features/reports/pages/ReportsList")),
    Payment: {
      List: lazy(() => import("../features/payments/pages/CompanyPaymentList")),
      Add: lazy(() => import("../features/payments/pages/CompanyAddPayment")),
      AddTransfer: lazy(() =>import("../features/payments/pages/AddEmployeeTransfer")),
      Edit: lazy(() =>import("../features/payments/pages/CompanyEditPayment")),
      View: lazy(() => import("../features/payments/pages/CompanyViewPayment")),
    },

    Surveys: {
      List: lazy(() => import("../features/surveys/pages/SurveyList")), // 🚀 UNIFIED RBAC
      Add: lazy(() => import("../features/surveys/pages/AddSurvey")), // 🚀 UNIFIED RBAC
      Edit: lazy(() => import("../features/surveys/pages/EditSurvey")), // 🚀 UNIFIED RBAC
      View: lazy(() => import("../features/surveys/pages/ViewSurvey")), // 🚀 UNIFIED RBAC
    },
    Users: {
      List: lazy(() => import("../features/users/pages/CompanyUserList")),
      Add: lazy(() =>import("../features/users/pages/CompanyAddUser")),
      Edit: lazy(() =>import("../features/users/pages/CompanyEditUser")),
      View: lazy(() =>import("../features/users/pages/CompanyViewUser")),
      BulkUpload: lazy(() =>import("../features/users/pages/CompanyBulkUser")),
      TodaysBirthdays: lazy(() =>import("../features/users/pages/TodaysBirthdays")),
    },
  },
  // User block removed; TenantAdmin components reused for both roles
};

// 🚀 MODERN APPROACH: No more helper functions needed!
// ProtectedLayout handles all authentication at layout level
// Individual routes are just simple { path, element } objects

// 🛡️ ERROR ROUTES: Public error pages (no auth required)
const errorRoutes = [
  { path: PATHS.UNAUTHORIZED, element: <Error401 /> },
  { path: PATHS.FORBIDDEN, element: <Error403 /> },
  { path: PATHS.ACCESS_DENIED, element: <PageComponents.Public.AccessDenied /> },
  { path: PATHS.NOT_FOUND, element: <Error404 /> },
  { path: PATHS.SERVER_ERROR, element: <Error500 /> },
  { path: PATHS.SERVICE_UNAVAILABLE, element: <Error503 /> },
];

// 🌐 PUBLIC ROUTES: No authentication required
const publicRoutes = [
  { path: PATHS.ROOT, element: <Navigate to={PATHS.LOGIN} replace /> },
  { path: PATHS.LOGIN, element: <PageComponents.Public.Login /> },
  { path: PATHS.VERIFY, element: <PageComponents.Public.Verify /> }, // 🔐 Verify handled by PublicLayout
  { path: PATHS.GO, element: <PageComponents.Public.Go /> },
];

// 🎯 SHARED DASHBOARD ROUTE - Used by all roles
// Dashboard component internally handles role-based content rendering
const sharedDashboardRoute = {
  path: PATHS.ADMIN.DASHBOARD, // "/dashboard" - same for all roles
  element: (
    <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.TENANT_ADMIN, ROLES.USER]}>
      <PageComponents.Admin.Dashboard />
    </ProtectedRoute>
  )
};

// 🔐 ADMIN ROUTES: Protected by ProtectedLayout (Admin role required)
// ProtectedLayout handles authentication, ProtectedRoute inside layout handles authorization
const adminRoutes = [
  // Root redirect to dashboard
  { 
    path: PATHS.ADMIN.ROOT, 
    element: <Navigate to={PATHS.ADMIN.DASHBOARD} replace /> 
  },
  
  // Dashboard - Shared across all roles
  sharedDashboardRoute,
  
  // Profile (no module requirement)
  { 
    path: PATHS.ADMIN.PROFILE, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
        <PageComponents.Admin.Profile />
      </ProtectedRoute>
    )
  },
  
  // Companies (subTenants module)
  { 
    path: PATHS.ADMIN.COMPANIES.ROOT, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="subTenants">
        <PageComponents.Admin.Companies.List />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.COMPANIES.ADD, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="subTenants">
        <PageComponents.Admin.Companies.Add />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.COMPANIES.EDIT, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="subTenants">
        <PageComponents.Admin.Companies.Edit />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.COMPANIES.VIEW, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="subTenants">
        <PageComponents.Admin.Companies.View />
      </ProtectedRoute>
    )
  },
  
  // Brands (brands module)
  { 
    path: PATHS.ADMIN.BRANDS.ROOT, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="brands">
        <PageComponents.Admin.Brands.List />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.BRANDS.ADD, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="brands">
        <PageComponents.Admin.Brands.Add />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.BRANDS.EDIT, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="brands">
        <PageComponents.Admin.Brands.Edit />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.BRANDS.VIEW, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="brands">
        <PageComponents.Admin.Brands.View />
      </ProtectedRoute>
    )
  },
  
  // Campaigns (campaigns module)
  { 
    path: PATHS.ADMIN.CAMPAIGNS.ROOT, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="campaigns">
        <PageComponents.Admin.Campaigns.List />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.CAMPAIGNS.ADD, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="campaigns">
        <PageComponents.Admin.Campaigns.Add />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.CAMPAIGNS.EDIT, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="campaigns">
        <PageComponents.Admin.Campaigns.Edit />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.CAMPAIGNS.VIEW, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="campaigns">
        <PageComponents.Admin.Campaigns.View />
      </ProtectedRoute>
    )
  },
  
  // Draws (draws module)
  { 
    path: PATHS.ADMIN.DRAWS.ROOT, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="draws">
        <PageComponents.Admin.Draws.List />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.DRAWS.ADD, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="draws">
        <PageComponents.Admin.Draws.Add />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.DRAWS.EDIT, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="draws">
        <PageComponents.Admin.Draws.Edit />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.DRAWS.VIEW, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="draws">
        <PageComponents.Admin.Draws.View />
      </ProtectedRoute>
    )
  },
  
  // Videos (videos module)
  { 
    path: PATHS.ADMIN.VIDEOS.ROOT, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="videos">
        <PageComponents.Admin.Videos.List />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.VIDEOS.ADD, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="videos">
        <PageComponents.Admin.Videos.Add />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.VIDEOS.EDIT, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="videos">
        <PageComponents.Admin.Videos.Edit />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.VIDEOS.VIEW, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="videos">
        <PageComponents.Admin.Videos.View />
      </ProtectedRoute>
    )
  },
  
  // Announcements (announcements module)
  { 
    path: PATHS.ADMIN.ANNOUNCEMENTS.ROOT, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="announcements">
        <PageComponents.Admin.Announcements.List />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.ANNOUNCEMENTS.ADD, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="announcements">
        <PageComponents.Admin.Announcements.Add />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.ANNOUNCEMENTS.EDIT, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="announcements">
        <PageComponents.Admin.Announcements.Edit />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.ANNOUNCEMENTS.VIEW, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="announcements">
        <PageComponents.Admin.Announcements.View />
      </ProtectedRoute>
    )
  },
  
  // Surveys (surveys module)
  { 
    path: PATHS.ADMIN.SURVEY.ROOT, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="surveys">
        <PageComponents.Admin.Surveys.List />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.SURVEY.ADD, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="surveys">
        <PageComponents.Admin.Surveys.Add />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.SURVEY.EDIT, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="surveys">
        <PageComponents.Admin.Surveys.Edit />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.SURVEY.VIEW, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="surveys">
        <PageComponents.Admin.Surveys.View />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.SURVEY.RECOMMENDATION, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="surveys">
        <PageComponents.Admin.Surveys.Recommendation />
      </ProtectedRoute>
    )
  },
  
  // Reports (reports module)
  { 
    path: PATHS.ADMIN.REPORTS, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="reports">
        <PageComponents.Admin.Reports />
      </ProtectedRoute>
    )
  },
  
  // Tickets (tickets module)
  { 
    path: PATHS.ADMIN.TICKETS.ROOT, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="tickets">
        <PageComponents.Admin.Tickets.List />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.TICKETS.ADD, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="tickets">
        <PageComponents.Admin.Tickets.Add />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.TICKETS.VIEW, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="tickets">
        <PageComponents.Admin.Tickets.View />
      </ProtectedRoute>
    )
  },
  
  // Payments (payments module)
  { 
    path: PATHS.ADMIN.PAYMENT.ROOT, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="payments">
        <PageComponents.Admin.Payment.List />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.PAYMENT.ADD, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="payments">
        <PageComponents.Admin.Payment.Add />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.PAYMENT.EDIT, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="payments">
        <PageComponents.Admin.Payment.Edit />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.PAYMENT.VIEW, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="payments">
        <PageComponents.Admin.Payment.View />
      </ProtectedRoute>
    )
  },
  
  // Gifts (gifts module)
  { 
    path: PATHS.ADMIN.GIFT.ROOT, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="gifts">
        <PageComponents.Admin.Gift.List />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.GIFT.ADD, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="gifts">
        <PageComponents.Admin.Gift.Add />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.GIFT.EDIT, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="gifts">
        <PageComponents.Admin.Gift.Edit />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.ADMIN.GIFT.VIEW, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]} requiredModule="gifts">
        <PageComponents.Admin.Gift.View />
      </ProtectedRoute>
    )
  },
];

// 🔐 TENANT/USER ROUTES: Protected by ProtectedLayout
// Note: Permission/module checks are handled by ProtectedRoute INSIDE AuthenticatedLayout
// ProtectedLayout only handles authentication (logged in or not)
const tenantAndUserRoutes = [
  // Root redirect to dashboard
  { 
    path: PATHS.TENANT_ADMIN.ROOT, 
    element: <Navigate to={PATHS.TENANT_ADMIN.DASHBOARD} replace /> 
  },
  
  // Dashboard - Shared across all roles
  sharedDashboardRoute,
  
  // Profile (no module requirement)
  { 
    path: PATHS.TENANT_ADMIN.PROFILE, 
    element: (
      <ProtectedRoute allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]}>
        <PageComponents.TenantAdmin.Profile />
      </ProtectedRoute>
    )
  },
  
  // Campaigns (campaigns module + permissions)
  { 
    path: PATHS.TENANT_ADMIN.CAMPAIGNS.ROOT, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="campaigns"
        requiredPermissions={["campaigns.read"]}
      >
        <PageComponents.TenantAdmin.Campaigns.List />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.CAMPAIGNS.ADD, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="campaigns"
        requiredPermissions={["campaigns.write"]}
      >
        <PageComponents.TenantAdmin.Campaigns.Add />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.CAMPAIGNS.EDIT, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="campaigns"
        requiredPermissions={["campaigns.write"]}
      >
        <PageComponents.TenantAdmin.Campaigns.Edit />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.CAMPAIGNS.VIEW, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="campaigns"
        requiredPermissions={["campaigns.read"]}
      >
        <PageComponents.TenantAdmin.Campaigns.View />
      </ProtectedRoute>
    )
  },
  
  // Announcements (announcements module + permissions)
  { 
    path: PATHS.TENANT_ADMIN.ANNOUNCEMENTS.ROOT, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="announcements"
        requiredPermissions={["announcements.read"]}
      >
        <PageComponents.TenantAdmin.Announcements.List />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.ANNOUNCEMENTS.ADD, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="announcements"
        requiredPermissions={["announcements.write"]}
      >
        <PageComponents.TenantAdmin.Announcements.Add />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.ANNOUNCEMENTS.EDIT, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="announcements"
        requiredPermissions={["announcements.write"]}
      >
        <PageComponents.TenantAdmin.Announcements.Edit />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.ANNOUNCEMENTS.VIEW, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="announcements"
        requiredPermissions={["announcements.read"]}
      >
        <PageComponents.TenantAdmin.Announcements.View />
      </ProtectedRoute>
    )
  },
  
  // Surveys (surveys module + permissions)
  { 
    path: PATHS.TENANT_ADMIN.SURVEY.ROOT, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="surveys"
        requiredPermissions={["surveys.read"]}
      >
        <PageComponents.TenantAdmin.Surveys.List />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.SURVEY.ADD, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="surveys"
        requiredPermissions={["surveys.write"]}
      >
        <PageComponents.TenantAdmin.Surveys.Add />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.SURVEY.EDIT, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="surveys"
        requiredPermissions={["surveys.write"]}
      >
        <PageComponents.TenantAdmin.Surveys.Edit />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.SURVEY.VIEW, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="surveys"
        requiredPermissions={["surveys.read"]}
      >
        <PageComponents.TenantAdmin.Surveys.View />
      </ProtectedRoute>
    )
  },
  
  // Draws (draws module + permissions)
  { 
    path: PATHS.TENANT_ADMIN.DRAWS.ROOT, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="draws"
        requiredPermissions={["draws.read"]}
      >
        <PageComponents.TenantAdmin.Draws.List />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.DRAWS.ADD, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="draws"
        requiredPermissions={["draws.write"]}
      >
        <PageComponents.TenantAdmin.Draws.Add />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.DRAWS.EDIT, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="draws"
        requiredPermissions={["draws.write"]}
      >
        <PageComponents.TenantAdmin.Draws.Edit />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.DRAWS.VIEW, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="draws"
        requiredPermissions={["draws.read"]}
      >
        <PageComponents.TenantAdmin.Draws.View />
      </ProtectedRoute>
    )
  },
  
  // Videos (videos module + permissions)
  { 
    path: PATHS.TENANT_ADMIN.VIDEOS.ROOT, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="videos"
        requiredPermissions={["videos.read"]}
      >
        <PageComponents.TenantAdmin.Videos.List />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.VIDEOS.ADD, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="videos"
        requiredPermissions={["videos.write"]}
      >
        <PageComponents.TenantAdmin.Videos.Add />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.VIDEOS.EDIT, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="videos"
        requiredPermissions={["videos.write"]}
      >
        <PageComponents.TenantAdmin.Videos.Edit />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.VIDEOS.VIEW, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="videos"
        requiredPermissions={["videos.read"]}
      >
        <PageComponents.TenantAdmin.Videos.View />
      </ProtectedRoute>
    )
  },
  
  // Brands (brands module + permissions)
  { 
    path: PATHS.TENANT_ADMIN.BRANDS.ROOT, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="brands"
        requiredPermissions={["brands.read"]}
      >
        <PageComponents.TenantAdmin.Brands.List />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.BRANDS.ADD, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="brands"
        requiredPermissions={["brands.write"]}
      >
        <PageComponents.TenantAdmin.Brands.Add />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.BRANDS.EDIT, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="brands"
        requiredPermissions={["brands.write"]}
      >
        <PageComponents.TenantAdmin.Brands.Edit />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.BRANDS.VIEW, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="brands"
        requiredPermissions={["brands.read"]}
      >
        <PageComponents.TenantAdmin.Brands.View />
      </ProtectedRoute>
    )
  },
  
  // Reports (reports module + permissions)
  { 
    path: PATHS.TENANT_ADMIN.REPORTS, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="reports"
        requiredPermissions={["reports.read"]}
      >
        <PageComponents.TenantAdmin.Reports />
      </ProtectedRoute>
    )
  },
  
  // Tickets (tickets module + permissions)
  { 
    path: PATHS.TENANT_ADMIN.TICKETS.ROOT, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="tickets"
        requiredPermissions={["tickets.read"]}
      >
        <PageComponents.TenantAdmin.Tickets.List />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.TICKETS.ADD, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="tickets"
        requiredPermissions={["tickets.write"]}
      >
        <PageComponents.TenantAdmin.Tickets.Add />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.TICKETS.VIEW, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="tickets"
        requiredPermissions={["tickets.read"]}
      >
        <PageComponents.TenantAdmin.Tickets.View />
      </ProtectedRoute>
    )
  },
  
  // Users (users module + permissions - TenantAdmin and User with permissions)
  { 
    path: PATHS.TENANT_ADMIN.USERS.ROOT, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="users"
        requiredPermissions={["users.read"]}
      >
        <PageComponents.TenantAdmin.Users.List />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.USERS.ADD, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="users"
        requiredPermissions={["users.write"]}
      >
        <PageComponents.TenantAdmin.Users.Add />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.USERS.EDIT, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="users"
        requiredPermissions={["users.write"]}
      >
        <PageComponents.TenantAdmin.Users.Edit />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.USERS.BIRTHDAYS, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="users"
        requiredPermissions={["users.read"]}
      >
        <PageComponents.TenantAdmin.Users.TodaysBirthdays />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.USERS.VIEW, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="users"
        requiredPermissions={["users.read"]}
      >
        <PageComponents.TenantAdmin.Users.View />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.USERS.BULK_UPLOAD, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="users"
        requiredPermissions={["users.write"]}
      >
        <PageComponents.TenantAdmin.Users.BulkUpload />
      </ProtectedRoute>
    )
  },
  
  // Company subTenants (subTenants module + permissions - TenantAdmin and User with permissions)
  { 
    path: PATHS.TENANT_ADMIN.COMPANY_SUBTENANTS.ROOT, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="subTenants"
        requiredPermissions={["subTenants.read"]}
      >
        <PageComponents.TenantAdmin.CompanysubTenants.List />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.COMPANY_SUBTENANTS.ADD, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="subTenants"
        requiredPermissions={["subTenants.write"]}
      >
        <PageComponents.TenantAdmin.CompanysubTenants.Add />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.COMPANY_SUBTENANTS.EDIT, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="subTenants"
        requiredPermissions={["subTenants.write"]}
      >
        <PageComponents.TenantAdmin.CompanysubTenants.Edit />
      </ProtectedRoute>
    )
  },
  { 
    path: PATHS.TENANT_ADMIN.COMPANY_SUBTENANTS.VIEW, 
    element: (
      <ProtectedRoute 
        allowedRoles={[ROLES.TENANT_ADMIN, ROLES.USER]} 
        requiredModule="subTenants"
        requiredPermissions={["subTenants.read"]}
      >
        <PageComponents.TenantAdmin.CompanysubTenants.View />
      </ProtectedRoute>
    )
  },
];

// 🚀 MODERN ROUTE CONFIGURATION
// Two-tier protection:
// 1. PublicLayout - No authentication needed
// 2. ProtectedLayout - Authentication required (handles redirect to login)
//    └── AuthenticatedLayout - Role/permission checks happen here
const routesConfig = [
  // PUBLIC ROUTES: No authentication required
  {
    path: "/",
    element: <PublicLayout />,
    children: [
      ...publicRoutes,   // /login, /go
      ...errorRoutes,    // /not-found, /forbidden, etc.
    ],
  },
  
  // PROTECTED ROUTES: Authentication required
  // ProtectedLayout checks if user is logged in
  // If not logged in → redirects to /login
  // If logged in → renders AuthenticatedLayout with role/permission checks
  {
    path: "/",
    element: <ProtectedLayout />,
    children: [
      ...adminRoutes,                // /admin/* routes
      ...tenantAndUserRoutes,        // /user/* routes
    ],
  },
  
  // 🛡️ CATCH-ALL 404: Any unmatched route goes to 404 page
  // This must be LAST in the routes array
  {
    path: "*",
    element: <Error404 />,
  },
];

export { PATHS, ROLES };
export default routesConfig;
